/**
 * @fileoverview Input/output schemas for plot-thread-snapshotter-v1.
 *
 * This agent performs an incremental update of plot thread data. Given the
 * complete set of plot threads known so far and the content of a single
 * chapter, it returns a DELTA that describes which threads were newly
 * introduced and which existing threads changed state. Consumer hooks apply
 * the delta to the accumulator they passed in as `existingPlotThreads`.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// EXISTING PLOT THREAD INPUT SHAPE
// =============================================================================

/**
 * Shape of an existing plot thread passed to the agent as prior context.
 *
 * The agent references entries by 0-based index into the array when it
 * emits thread updates in the output.
 */
const ExistingPlotThreadSchema = z.object({
  title: z.string(),
  summary: z.string(),
  threadQuestion: z.string(),
  characterNames: z.array(z.string()),
  status: z.enum(['active', 'resolved']),
  introducedInChapterNumber: z.number().nullable(),
  resolvedInChapterNumber: z.number().nullable(),
  activeInChapterNumbers: z.array(z.number()),
});

export type ExistingPlotThread = z.infer<typeof ExistingPlotThreadSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * Uses existing prompt variables:
 * - textInput: The content of the single chapter being analyzed
 * - chapterDirectory: Lightweight chapter list — the only story-wide
 *   context the agent needs; broader storyContext is intentionally
 *   omitted to keep the prompt focused as the catalog grows.
 *
 * Adds:
 * - existingPlotThreads: All plot threads known so far (may be empty for
 *   the first chapter of an import). The agent returns a delta against
 *   this list; the consumer carries unmentioned threads forward unchanged.
 * - targetChapterNumber: The chapter number of the chapter whose text is
 *   provided in textInput. Used to scope new-thread attribution and to
 *   update activeInChapterNumbers / resolvedInChapterNumber.
 * - narrativeProgress: Information about how far through the story we are
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.textInput.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterDirectory.schema.shape,
  existingPlotThreads: z.array(ExistingPlotThreadSchema).optional().describe(
    'Plot threads already known from previously analyzed chapters. The agent ' +
    'returns a delta against this list.'
  ),
  targetChapterNumber: z.number().optional().describe(
    'The chapter number of the chapter whose content is provided in textInput.'
  ),
  narrativeProgress: z.string().optional().describe(
    'How far through the story the narrative currently is, e.g. ' +
    '"Chapter 5 of ~20 planned (25%)" or "12,000 of ~80,000 projected words (15%)". ' +
    'Helps the agent distinguish threads that are unresolved because the story ' +
    'is incomplete vs. threads that are genuinely left open.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA (Delta — flat structure for Gemini compatibility)
// =============================================================================
//
// The agent returns a DELTA rather than the full catalog. Callers apply the
// delta to the accumulator they passed in as `existingPlotThreads`:
// - Threads not mentioned in `threadUpdates` are carried forward unchanged.
// - Entries in `threadUpdates` modify the existing thread at the given index.
// - Entries in `newThreads` are appended to the catalog and are assumed to be
//   introduced in the target chapter.

/**
 * A new plot thread introduced in the target chapter.
 *
 * Fields the consumer derives from the target chapter:
 * - introducedInChapterNumber = targetChapterNumber
 * - activeInChapterNumbers = [targetChapterNumber]
 * - resolvedInChapterNumber = targetChapterNumber if resolvedInThisChapter else null
 * - status = 'resolved' if resolvedInThisChapter else 'active'
 */
const NewPlotThreadOutputSchema = z.object({
  title: z.string().describe(
    'Short, evocative title for the thread (2-5 words). ' +
    'Examples: "The Police Chase", "Sibling Rivalry", "The Locked Room".'
  ),
  summary: z.string().describe(
    'What this thread is about — the context that makes the question meaningful (1-3 sentences). ' +
    'Example: "Captain Fache pursues Langdon through Paris, convinced he murdered the curator."'
  ),
  threadQuestion: z.string().describe(
    'The dramatic question driving this thread. Must be phrased as a question. ' +
    'Example: "Will they catch him before he proves his innocence?"'
  ),
  characterNames: z.array(z.string()).describe(
    'Names of characters directly involved in this thread.'
  ),
  resolvedInThisChapter: z.boolean().describe(
    'True if the thread is both introduced and conclusively resolved in the target chapter. ' +
    'False if the thread is introduced here but remains open.'
  ),
});

/**
 * An update to an existing plot thread, referenced by 0-based index into
 * the `existingPlotThreads` input array.
 *
 * Only emit an update when the thread genuinely changes in the target
 * chapter. Threads that are merely referenced (callback, name-drop, recap)
 * should NOT appear in threadUpdates — omit them and the consumer will
 * carry them forward unchanged.
 */
const ThreadUpdateOutputSchema = z.object({
  index: z.number().int().min(0).describe(
    '0-based index into the existingPlotThreads input array. Must reference an existing thread.'
  ),
  activeInThisChapter: z.boolean().describe(
    'True if the thread is genuinely active in the target chapter (its scenario is on-page, ' +
    'the dramatic question is pressed, or new developments occur). If false, omit this update entirely.'
  ),
  resolvedInThisChapter: z.boolean().describe(
    'True if the target chapter conclusively answers the thread\'s driving question. ' +
    'Implies activeInThisChapter is also true (a thread is active in the chapter where it resolves).'
  ),
  newCharacterNames: z.array(z.string()).optional().describe(
    'Names of characters who became involved in this thread in the target chapter. ' +
    'Omit or leave empty if no new characters joined.'
  ),
});

/**
 * Output schema for the plot-thread-snapshotter-v1 agent.
 *
 * Flat structure (3 levels max) for Gemini API compatibility:
 * - Level 1: Root object
 * - Level 2: newThreads / threadUpdates arrays
 * - Level 3: Array items
 */
export const OutputSchema = z.object({
  newThreads: z.array(NewPlotThreadOutputSchema).describe(
    'Plot threads introduced in the target chapter. Empty if no new threads were introduced.'
  ),
  threadUpdates: z.array(ThreadUpdateOutputSchema).describe(
    'Updates to existing plot threads that genuinely change in the target chapter. ' +
    'Existing threads not referenced here are carried forward unchanged.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;

// Type exports for consumer convenience
export type NewPlotThreadOutput = z.infer<typeof NewPlotThreadOutputSchema>;
export type ThreadUpdateOutput = z.infer<typeof ThreadUpdateOutputSchema>;
